import React from 'react'
import { SearchDropdownController } from './controller'
import { SearchDropdownView } from './view'
import { defaultFilter } from 'cmdk'

export type SearchDropdownProps = {
  /**
   * HTML id
   */
  id?: string

  /**
   * HTML name attribute for the search input.
   */
  name?: string

  /**
   * Visual spacing variant of the component.
   * - "default": uses standard spacing like a regular shadcn input component
   * - "big": mimics the spacing of the SearchAutocomplete widget
   */
  variant?: "default" | "big"

  /**
   * Currently selected value.
   * `null` indicates no selection.
   */
  value: string | null

  /**
   * Called when the selected value changes.
   */
  onChange: (value: string | null) => void

  /**
   * Current search value.
   * If omitted, the component manages its own internal search state.
   */
  search?: string

  /**
   * Optional debounce before updating/filtering results, after search value has changed
   */
  debounceTime?: number

  /**
   * Minimum amount of characters required before showing/fetching results.
   * Can help when options are fetched from slow sources.
   * @default 0
   */
  minSearchLength?: number

  /**
   * Callback fired when the search value changes.
   * Provide this together with `search` to fully control the search state.
   */
  onSearch?: (search: string) => void

  /**
   * Optional callback fired when the open/closed state changes.
   */
  onOpenChange?: (open: boolean) => void

  /**
   * Options to display.
   * Can be a flat list or grouped options.
   */
  options: SearchOption[] | GroupedSearchOptions

  /**
   * Error that occurred while fetching options.
   * When provided, the dropdown is rendered in an error state.
   */
  fetchingError?: Error | null

  /**
   * Keep the search input in sync with the selected option's label.
   */
  syncSearchWithLabel?: boolean

  /**
   * Optionally set to `false` to turn off the automatic filtering and sorting.
   * If `false`, you must conditionally render valid items based on the search query yourself.
   */
  shouldFilter?: boolean

  /**
   * Callback converting an option value to a label
   * ONLY necessary if the option is not already provided in the {@link SearchDropdownProps.options} list
   * (e.g. if you use server side searching)
   * @param value the value to convert to a label
   */
  getLabelForOptionValue?: (value: string) => Promise<string>

  /**
   * Whether search terms should be highlighted in option labels.
   * Has no effect if {@link SearchDropdownProps.renderOptionLabel} is set;
   * in that case consumers should use {@link HighlightedLabel} manually if needed.
   */
  highlightWords?: boolean

  /**
   * Shows a loading indicator while options are being fetched.
   */
  isLoading?: boolean

  /**
   * Configuration for UI text labels
   */
  labelConfig?: {
    placeholder?: string
    noOptionsFound?: string
    loadingOptions?: string
    errorFetchingOptions?: string
  }

  /**
   * Custom CSS classes
   */
  classNames?: {
    commandWrapper?: string
    inputWrapper?: string
    input?: string
    popover?: string
    list?: string
    group?: string
    item?: string
  }

  /**
   * Custom renderer for a single option item.
   */
  renderOptionLabel?: (option: SearchOption) => React.ReactNode

  /**
   * Custom renderer for a group header.
   */
  renderGroupHeading?: (group: SearchOptionGroup) => React.ReactNode

  /**
   * Custom filter function
  */
  filterFn?: typeof defaultFilter
}

/**
 * Single selectable option.
 * Consumers may want to extend this type to attach additional data for custom render props.
 */
export type SearchOption = {
  label: string
  value: string
  icon?: string
}

/** Special key representing options without an explicit group. */
export const UNGROUPED = Symbol('ungrouped')

/**
 * Group of related search options, optionally labeled and iconized.
 * Consumers may want to extend this type to attach additional data for custom render props.
 */
export type SearchOptionGroup = {
  label?: string
  icon?: string
  options: SearchOption[]
}

export type GroupedSearchOptions = Map<string | typeof UNGROUPED, SearchOptionGroup>

/**
 * Similar to {@link SearchSelect} but with
 * - modified appearance (no "double" inputs)
 * - optionally controlled search input
 * - optional support for remote/async options
 * - TODO
 */
export function SearchDropdown(props: SearchDropdownProps) {
  return (
    <SearchDropdownController {...props}>
      <SearchDropdownView />
    </SearchDropdownController>
  )
}
